import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { EnterpriseIcon } from "@/components/enterprise/enterpriseIcons";
import { SandboxedEmbed } from "@/components/common/SandboxedEmbed";

const useEnterprise = () =>
  useQuery({ queryKey: ["enterprise"], queryFn: async () => (await api.get("/enterprise")).data });

const Para = ({ text }) =>
  (text || "").split("\n\n").map((p, i) => (
    <p key={i} className="text-base leading-relaxed text-slate-600">{p}</p>
  ));

const CtaLink = ({ target, label, testid }) => {
  if (!label) return null;
  const isAnchor = (target || "").startsWith("#");
  const cls =
    "inline-flex items-center gap-2 rounded-full bg-teal-700 px-6 py-3 text-base font-semibold text-white shadow-sm transition-colors hover:bg-teal-800";
  return isAnchor ? (
    <a href={target} className={cls} data-testid={testid}>{label}<ArrowRight className="h-4 w-4" /></a>
  ) : (
    <Link to={target || "/contact"} className={cls} data-testid={testid}>{label}<ArrowRight className="h-4 w-4" /></Link>
  );
};

export default function Enterprise() {
  const { data: c, isLoading } = useEnterprise();
  useEffect(() => {
    document.title = "Teams & Enterprise — Startup Otter";
  }, []);
  if (isLoading || !c) return <div className="min-h-[60vh]" data-testid="enterprise-loading" />;

  return (
    <div className="bg-white" data-testid="enterprise-page">

      {/* Hero */}
      <section className="bg-teal-700">
        <div className="mx-auto max-w-5xl px-6 py-20 text-center md:py-28">
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
            {c.hero_headline}
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-teal-50">
            {c.hero_subheadline}
          </p>
          <div className="mt-9">
            <CtaLink target={c.hero_cta_target} label={c.hero_cta_label} testid="enterprise-hero-cta" />
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="mx-auto max-w-3xl px-6 py-16 text-center md:py-20">
        <h2 className="text-3xl font-bold tracking-tight text-slate-900">{c.intro_heading}</h2>
        <div className="mt-5 space-y-4">
          <Para text={c.intro_body} />
        </div>
      </section>

      {/* Offerings */}
      {(c.offerings || []).length > 0 && (
        <section className="border-y border-slate-100 bg-slate-50">
          <div className="mx-auto max-w-6xl px-6 py-16 md:py-20">
            <h2 className="text-center text-3xl font-bold tracking-tight text-slate-900">
              {c.offerings_heading}
            </h2>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {c.offerings.map((o, i) => (
                <div key={i} className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-100" data-testid={`enterprise-offering-${i}`}>
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
                    <EnterpriseIcon name={o.icon} className="h-6 w-6" />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-slate-900">{o.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{o.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Benefits */}
      {(c.benefits || []).length > 0 && (
        <section className="mx-auto max-w-3xl px-6 py-16 md:py-20">
          <h2 className="text-center text-3xl font-bold tracking-tight text-slate-900">{c.benefits_heading}</h2>
          <ul className="mx-auto mt-10 max-w-xl space-y-4">
            {c.benefits.map((b, i) => (
              <li key={i} className="flex items-start gap-3" data-testid={`enterprise-benefit-${i}`}>
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal-100 text-teal-700">
                  <Check className="h-4 w-4" />
                </span>
                <span className="text-base leading-relaxed text-slate-700">{b}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Form / embed */}
      <section id="enterprise-form" className="scroll-mt-24 border-t border-slate-100 bg-slate-50">
        <div className="mx-auto max-w-6xl px-6 py-16 md:py-20">
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900">{c.form_heading}</h2>
            <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-slate-600">{c.form_intro}</p>
          </div>
          <div className="mt-10 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-100 md:p-8">
            {c.form_embed_html ? (
              <div data-testid="enterprise-form-embed">
                <SandboxedEmbed html={c.form_embed_html} title="Enterprise contact form" minHeight={520} />
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center" data-testid="enterprise-form-fallback">
                <p className="text-sm text-slate-500">
                  Reach our team at{" "}
                  <a href="mailto:hello@startupotter.com" className="font-semibold text-teal-700 hover:underline">
                    hello@startupotter.com
                  </a>{" "}
                  and we'll build the right plan for your organization.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-teal-700">
        <div className="mx-auto max-w-4xl px-6 py-16 text-center md:py-20">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">{c.final_heading}</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-teal-50">{c.final_body}</p>
          <div className="mt-9">
            <CtaLink target={c.final_cta_target} label={c.final_cta_label} testid="enterprise-final-cta" />
          </div>
        </div>
      </section>
    </div>
  );
}
